import { prepareBrowserImageUpload } from "./browserImageUpload";
import {
  isSupportedReferenceImage,
  REFERENCE_IMAGE_DATA_URL_MAX_LENGTH,
  REFERENCE_IMAGE_MAX_COUNT,
  REFERENCE_IMAGE_MAX_INPUT_BYTES,
  REFERENCE_IMAGE_PREPARED_MAX_BYTES,
  REFERENCE_IMAGE_PREPARED_MAX_MB,
} from "./referenceImageUpload";

export interface PreparedReferenceImage {
  file: File;
  dataUrl: string;
}

export interface PreparedReferenceImageResult {
  entries: PreparedReferenceImage[];
  rejectedCount: number;
}

export function describeReferenceImageSelection(
  addedCount: number,
  rejectedCount: number
): string | null {
  if (rejectedCount <= 0) return null;
  const addedLabel = addedCount === 1 ? "image" : "images";
  const skippedLabel = rejectedCount === 1 ? "image" : "images";
  return `${addedCount} ${addedLabel} added, ${rejectedCount} ${skippedLabel} skipped.`;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Applies the complete client-side reference-image contract used by every
 * create entry point: format/size validation, count limiting, normalization,
 * compression, and the final data-URL ceiling enforced by the API.
 */
export async function prepareReferenceImages(
  files: readonly File[]
): Promise<PreparedReferenceImageResult> {
  const acceptedFiles = files.filter(
    file =>
      isSupportedReferenceImage(file) &&
      file.size <= REFERENCE_IMAGE_MAX_INPUT_BYTES
  );
  const selectedFiles = acceptedFiles.slice(0, REFERENCE_IMAGE_MAX_COUNT);
  // Treat each file as an independent candidate. A corrupt image can carry a
  // valid MIME type and still fail during decode/compression; it must not make
  // the customer's other valid references disappear with it.
  const candidateResults = await Promise.allSettled(
    selectedFiles.map(async file => {
      const preparedFile = await prepareBrowserImageUpload(file, {
        maxInputBytes: REFERENCE_IMAGE_MAX_INPUT_BYTES,
        maxOutputBytes: REFERENCE_IMAGE_PREPARED_MAX_BYTES,
        maxWidth: 1600,
        maxHeight: 1600,
        quality: 0.8,
        targetFileSizeMB: REFERENCE_IMAGE_PREPARED_MAX_MB,
      });
      return {
        file: preparedFile,
        dataUrl: await readFileAsDataUrl(preparedFile),
      };
    })
  );
  const candidateEntries = candidateResults.flatMap(result =>
    result.status === "fulfilled" ? [result.value] : []
  );
  const failedPreparationCount =
    candidateResults.length - candidateEntries.length;

  const acceptedEntries = candidateEntries.filter(
    entry =>
      entry.file.size <= REFERENCE_IMAGE_PREPARED_MAX_BYTES &&
      entry.dataUrl.length <= REFERENCE_IMAGE_DATA_URL_MAX_LENGTH
  );

  return {
    entries: acceptedEntries,
    rejectedCount:
      files.length -
      selectedFiles.length +
      failedPreparationCount +
      (candidateEntries.length - acceptedEntries.length),
  };
}

/**
 * Stores prepared references for durable chat/session history. Callers retain
 * the inline data URLs as a safe fallback when storage is unavailable.
 */
export async function uploadPreparedReferenceImages(
  dataUrls: string[],
  side: "front" | "back"
): Promise<string[] | null> {
  if (
    dataUrls.length === 0 ||
    dataUrls.length > REFERENCE_IMAGE_MAX_COUNT ||
    dataUrls.some(
      dataUrl => dataUrl.length > REFERENCE_IMAGE_DATA_URL_MAX_LENGTH
    )
  ) {
    return null;
  }

  try {
    const response = await fetch("/api/create/upload-reference", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ side, images: dataUrls }),
    });
    if (!response.ok) return null;
    const payload = (await response.json()) as { urls?: unknown };
    if (
      !Array.isArray(payload.urls) ||
      payload.urls.length !== dataUrls.length ||
      !payload.urls.every(
        (url): url is string => typeof url === "string" && url.length > 0
      )
    ) {
      // A malformed or stale server response must not replace the complete
      // inline batch with fewer references; preserve the caller's fallback.
      return null;
    }
    return payload.urls;
  } catch {
    return null;
  }
}
